/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component. Matches the same exemption on
   DimeTreeTable/use-tree-table.ts. */
/* eslint-disable max-lines -- one state machine (display/editing, commit-settle, refocus,
   auto-edit-on-focus) whose steps depend on each other; splitting it across files would make the
   ordering between its several refs and effects harder to verify, not easier. */
import * as React from 'react';

import { findAdjacentFocusable } from './find-adjacent-focusable.js';

export type EditableCellMode = 'display' | 'editing';

export interface UseEditableCellArgs<TValue> {
  value: TValue | null;
  onCommit: (next: TValue | null) => void | Promise<void>;
  format: (value: TValue | null) => string;
  parse: (raw: string) => TValue | null;
  isSaving?: boolean;
  disabled?: boolean;
  onCancel?: () => void;
  commitUnchanged?: boolean;
}

export interface UseEditableCellResult {
  mode: EditableCellMode;
  draftText: string;
  setDraftText: (next: string) => void;
  enterEditMode: () => void;
  handleInputKeyDown: (evt: React.KeyboardEvent<HTMLInputElement>) => void;
  handleInputBlur: () => void;
  /** Clears the draft and commits immediately (`onCommit(null)`), rather than just emptying the
   *  input and waiting for the user's own Enter/blur to follow -- a clear button exists precisely
   *  so that second step isn't necessary. */
  handleClear: () => void;
  /** Focused imperatively on entering edit mode — not `autoFocus`, which `jsx-a11y/no-autofocus`
   *  flags for the page-load-focus-stealing case this isn't: focusing the input the user just
   *  asked to edit, in response to their own click/Enter, is the expected behavior here. */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** Imperatively refocused once edit mode actually exits. */
  displayRef: React.RefObject<HTMLButtonElement | null>;
  /** Wire to the display button's `onFocus` — opens edit mode the moment focus actually lands on
   *  it (Tab from a previous cell, or a fresh click), so a cell Tab moves into is immediately
   *  editable instead of showing as a plain button until clicked. */
  handleDisplayFocus: () => void;
}

export function useEditableCell<TValue>({
  value,
  onCommit,
  format,
  parse,
  isSaving = false,
  disabled = false,
  onCancel,
  commitUnchanged = false,
}: UseEditableCellArgs<TValue>): UseEditableCellResult {
  const [mode, setMode] = React.useState<EditableCellMode>('display');
  const [draftText, setDraftText] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const displayRef = React.useRef<HTMLButtonElement>(null);

  // Bumped on every commit so the settle effect below re-checks even when `isSaving` itself
  // never changes (the fully synchronous case — see that effect).
  const [settleTick, setSettleTick] = React.useState(0);
  // A ref, not state: handleInputBlur reads this synchronously in the same tick a commit fires,
  // before React has necessarily re-rendered with the state update above reflected.
  const awaitingSettleRef = React.useRef(false);
  // Only Enter and Escape return focus to the display button once edit mode exits. Tab leaves
  // focus to the browser's own default handling, and an organic blur means the user already
  // moved focus somewhere themselves — neither should have it stolen back.
  const refocusPendingRef = React.useRef(false);
  // Set immediately before that same imperative refocus, so handleDisplayFocus below can tell
  // "we just sent focus back here after Enter/Escape/Clear" apart from "focus arrived here from
  // somewhere else" (Tab, or a fresh click) -- only the latter should reopen edit mode.
  const suppressAutoEditOnFocusRef = React.useRef(false);

  const keyedRef = React.useRef(false);

  const enterEditMode = React.useCallback(() => {
    if (disabled || isSaving) return;
    keyedRef.current = false;
    setDraftText(format(value));
    setMode('editing');
  }, [disabled, isSaving, format, value]);

  // Shared by commit() (the draft the user actually typed) and handleClear() (an explicit '',
  // regardless of what's currently in the input) -- both are "here is the raw text to resolve and
  // write," differing only in which text that is.
  const commitText = React.useCallback(
    (text: string) => {
      // An unchanged draft has nothing to write -- exit without touching onCommit at all, so a
      // click-away that didn't edit anything doesn't fire a mutation (or, on an inherited cell,
      // silently convert it to SET_HERE for no reason).
      if (text === format(value) && !(commitUnchanged && keyedRef.current)) {
        setMode('display');
        return;
      }
      awaitingSettleRef.current = true;
      setSettleTick((tick) => tick + 1);
      void onCommit(parse(text));
    },
    [format, value, parse, onCommit, commitUnchanged],
  );

  const commit = React.useCallback(() => commitText(draftText), [commitText, draftText]);

  const handleClear = React.useCallback(() => {
    setDraftText('');
    refocusPendingRef.current = true;
    commitText('');
  }, [commitText]);

  const cancelAndExit = React.useCallback(() => {
    refocusPendingRef.current = true;
    setMode('display');
    onCancel?.();
  }, [onCancel]);

  // Exits edit mode once the commit it's holding for has actually settled. Runs as a layout
  // effect so the fully synchronous case (isSaving never turns true at all) resolves before the
  // browser paints, rather than flashing a disabled input for one frame it will never need.
  //
  // The input deliberately stays mounted and merely disabled for the whole `isSaving` window
  // instead of collapsing back into the display button immediately on commit — collapsing early
  // would render the button from the *old* value/state props (the new ones haven't arrived yet),
  // then jump to the real value once they do. Staying put avoids that jump entirely: the input
  // keeps showing exactly what the user typed until there's something newer to show.
  React.useLayoutEffect(() => {
    if (!awaitingSettleRef.current || isSaving) return;
    awaitingSettleRef.current = false;
    setMode('display');
    // WHY: settleTick forces a re-check on every commit even when isSaving never changes.
  }, [isSaving, settleTick]);

  // Refocuses the display button once `mode` actually lands on 'display' in the DOM — doing it
  // here, rather than at the point the transition was requested, is what makes focus() land on a
  // button that exists rather than the stale ref from the render where it didn't yet.
  React.useLayoutEffect(() => {
    if (mode !== 'display' || !refocusPendingRef.current) return;
    refocusPendingRef.current = false;
    suppressAutoEditOnFocusRef.current = true;
    displayRef.current?.focus();
  }, [mode]);

  // Opens edit mode the instant the display button is focused, rather than waiting for a
  // separate click or Enter -- the whole point being that Tab's own `next.focus()`
  // (handleInputKeyDown below) lands here with the input already visible, not a plain button the
  // user has to click again. Suppressed for exactly one focus event
  // when that focus is this hook's own refocus-to-display after Enter/Escape/Clear, which must
  // land in display mode, not immediately reopen it.
  const handleDisplayFocus = React.useCallback(() => {
    if (suppressAutoEditOnFocusRef.current) {
      suppressAutoEditOnFocusRef.current = false;
      return;
    }
    enterEditMode();
  }, [enterEditMode]);

  // Symmetric with the effect above: focuses the input once `mode` lands on 'editing', for the
  // same reason -- the element doesn't exist in the DOM until the render this effect follows.
  // Also selects the whole draft so typing a replacement value doesn't require backspacing the
  // existing one first.
  React.useEffect(() => {
    const input = inputRef.current;
    if (mode !== 'editing' || !input) return;
    const markRetyped = () => {
      if (input.value === format(value)) keyedRef.current = true;
    };
    input.addEventListener('input', markRetyped);
    return () => input.removeEventListener('input', markRetyped);
  }, [mode, format, value]);

  React.useLayoutEffect(() => {
    if (mode !== 'editing') return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [mode]);

  const handleInputKeyDown = React.useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        refocusPendingRef.current = true;
        commit();
      } else if (evt.key === 'Escape') {
        evt.preventDefault();
        cancelAndExit();
      } else if (evt.key === 'Tab') {
        // Committing exits edit mode, removing this input from the DOM in the same synchronous
        // pass a native (or userEvent-simulated) default Tab action uses to compute "what's
        // focusable now" -- by the time it runs, this element is already gone, and the browser's
        // own algorithm falls back to document.body instead of advancing focus. Finding the
        // destination first, before anything commits, and focusing it explicitly sidesteps that
        // race instead of depending on timing that isn't guaranteed either way.
        const next = findAdjacentFocusable(evt.currentTarget, evt.shiftKey ? -1 : 1);
        if (next) {
          evt.preventDefault();
          commit();
          next.focus();
        }
        // No destination (e.g. this is the last/first focusable element in the document): fall
        // through to the browser's own default Tab handling, and still commit via the blur that
        // produces.
      }
    },
    [commit, cancelAndExit],
  );

  const handleInputBlur = React.useCallback(() => {
    // Enter and Tab both already call commit() themselves; a blur that follows either (Tab's
    // default focus move, or the eventual refocus after Enter settles) must not commit a second
    // time. awaitingSettleRef is already true by then, so this only fires commit() for an organic
    // blur — the user clicked or tabbed away by some path that didn't already handle it above.
    if (!awaitingSettleRef.current) commit();
  }, [commit]);

  return {
    mode,
    draftText,
    setDraftText,
    enterEditMode,
    handleInputKeyDown,
    handleInputBlur,
    handleClear,
    inputRef,
    displayRef,
    handleDisplayFocus,
  };
}
